'use es6';

import hubspot from 'hubspot';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { addModuleToFlexColumn, addModuleToLayoutSection, updateLayoutSectionToAddTo } from 'ContentEditorUI/redux/actions/moduleActions';
import ModuleSelectorModal from 'ContentEditorUI/components/modals/ModuleSelectorModal';
import { formatContainersFromStoreForSave } from 'ContentEditorUI/data/ContainerTransforms';
import { showErrorWithHTML } from 'ContentEditorUI/utils/errorUtils';
import inpageActionTypes from 'ContentEditorUI/redux/actions/inpageActionTypes';
import { updateScrollToModuleId } from 'ContentEditorUI/redux/actions/inpageActions';
import { moduleHasAnyJs } from 'ContentEditorUI/data/moduleUtils';
import { createNewModuleFromSchema } from 'ContentUtils/helpers/ModuleCreationHelpers';
import { getSchemaForId } from 'ContentEditorUI/redux/selectors/moduleSchemaSelectors';
import { isFormModule } from 'ContentEditorUI/redux/selectors/moduleSelectorHelpers';
import { createNewModuleSchemaJson } from 'ContentUtils/helpers/ModuleCreationHelpers';
import { getIsUngatedForMergeInDefaultValues } from 'ContentEditorUI/redux/selectors/authSelectors';
import { updateSelectedItem } from 'ContentEditorUI/redux/actions/selectionActions';
import { addCustomLayoutSection } from 'ContentEditorUI/redux/actions/moduleActions';
import { generateLayoutDataForAllDefaultColumns } from 'ContentEditorUI/containers/sections/utils';
import { createTreeFragmentForEmbed } from 'ContentEditorUI/utils/layoutSectionTreeUtils';
import { ThemeProvider } from 'foundations-theming/components/ThemeProvider';
import { addEmbedToEmbedInfo } from 'ContentEditorUI/redux/actions/moduleActions';
import { clearQueryParam } from 'ContentEditorUI/redux/actions/routingActions';
import { getModuleIdsByBuiltinType } from 'ContentEditorUI/redux/selectors/moduleSchemaSelectors';
import { LAYOUT_SECTION_ROW_APPENDED, LAYOUT_SECTION_ROW_PREPENDED, LAYOUT_SECTION_ROW_INSERTED_AFTER, LAYOUT_SECTION_ROW_INSERTED_BEFORE, LAYOUT_SECTION_COLUMN_INSERTED_AFTER, LAYOUT_SECTION_COLUMN_INSERTED_BEFORE, LAYOUT_SECTION_INSERTED_IN_EMPTY_COLUMN, LAYOUT_SECTION_INSERTED_IN_EMPTY_ROW, GRID_LAYOUTS_ELEMENT_INSERTED_BEFORE, GRID_LAYOUTS_ELEMENT_INSERTED_AFTER, GRID_LAYOUTS_INSERTED_IN_EMPTY_STACK, GRID_LAYOUTS_INSERTED_IN_EMPTY_GRID_LAYOUT, GRID_LAYOUTS_INSERTED_IN_TABLE_CELL, GRID_LAYOUTS_APPENDED_GRID_CELL, GRID_LAYOUTS_INSERTED_AFTER_TABLE_CELL, GRID_LAYOUTS_MOVE_SECTION_AFTER, GRID_LAYOUTS_MOVE_SECTION_BEFORE, GRID_LAYOUTS_WRAP_AND_INSERT_ABOVE_CONTAINER, GRID_LAYOUTS_WRAP_AND_INSERT_BELOW_CONTAINER, GRID_LAYOUTS_WRAP_AND_INSERT_LEFT_OF_CONTAINER, GRID_LAYOUTS_WRAP_AND_INSERT_RIGHT_OF_CONTAINER, GRID_LAYOUTS_DROP_ON_PEGBOARD } from 'ContentEditorUI/redux/actions/actionTypes';

// Only these Redux action types may be dispatched in response to a building
// block drop coming over the (untrusted) event bus from the preview iframe.
// Without this allowlist an attacker who can execute script in the iframe (e.g.
// via stored Head HTML) could dispatch arbitrary actions such as
// MERGE_MODULE_META_DATA to pollute Object.prototype.
// See https://github.com/HubSpotEngineering/pages/issues/5278.
import { jsx as _jsx } from "react/jsx-runtime";
const ALLOWED_BUILDING_BLOCK_ACTION_TYPES = new Set([LAYOUT_SECTION_ROW_APPENDED, LAYOUT_SECTION_ROW_PREPENDED, LAYOUT_SECTION_ROW_INSERTED_AFTER, LAYOUT_SECTION_ROW_INSERTED_BEFORE, LAYOUT_SECTION_COLUMN_INSERTED_AFTER, LAYOUT_SECTION_COLUMN_INSERTED_BEFORE, LAYOUT_SECTION_INSERTED_IN_EMPTY_COLUMN, LAYOUT_SECTION_INSERTED_IN_EMPTY_ROW, GRID_LAYOUTS_ELEMENT_INSERTED_BEFORE, GRID_LAYOUTS_ELEMENT_INSERTED_AFTER, GRID_LAYOUTS_INSERTED_IN_EMPTY_STACK, GRID_LAYOUTS_INSERTED_IN_EMPTY_GRID_LAYOUT, GRID_LAYOUTS_INSERTED_IN_TABLE_CELL, GRID_LAYOUTS_APPENDED_GRID_CELL, GRID_LAYOUTS_INSERTED_AFTER_TABLE_CELL, GRID_LAYOUTS_MOVE_SECTION_AFTER, GRID_LAYOUTS_MOVE_SECTION_BEFORE, GRID_LAYOUTS_WRAP_AND_INSERT_ABOVE_CONTAINER, GRID_LAYOUTS_WRAP_AND_INSERT_BELOW_CONTAINER, GRID_LAYOUTS_WRAP_AND_INSERT_LEFT_OF_CONTAINER, GRID_LAYOUTS_WRAP_AND_INSERT_RIGHT_OF_CONTAINER, GRID_LAYOUTS_DROP_ON_PEGBOARD]);
export default class WidgetAddController {
  constructor({
    bus,
    saveActions
  }) {
    this.saveAndRefreshCallback = ({
      module,
      shouldRefresh
    } = {}) => {
      if (shouldRefresh) {
        document.querySelector('.contentEditor-preview-loading').style.display = 'block';
      }
      this.saveActions.autoSave().then(() => {
        if (shouldRefresh) {
          this.bus.trigger('preview:refresh');
        }
      }).catch(() => {
        console.log('Failed to add module', module);
      });
    };
    this.addModuleToFlexColumn = ({
      order,
      widgetCid,
      containerId,
      setModuleAddedId = false
    }) => {
      const store = hubspot.ContentEditorUI.store;
      const reduxStateBeforeAdd = hubspot.ContentEditorUI.store.getState();
      const isUngatedForMergeInDefaultValues = getIsUngatedForMergeInDefaultValues(reduxStateBeforeAdd);
      // generate the "empty" module data
      const schema = getSchemaForId(reduxStateBeforeAdd, widgetCid);
      const module = createNewModuleFromSchema(schema, {
        setMergeInDefaultFieldValuesToFalse: isUngatedForMergeInDefaultValues
      });
      module.isInContainer = true;
      // Because modules in flex columns use "key" for some reason lost to time
      module.key = module.id;

      // update redux with module info
      store.dispatch(addModuleToFlexColumn(module, containerId, order, schema));
      this.bus.trigger('post:saveContainers', {
        containers: formatContainersFromStoreForSave()
      });

      // Need state after add
      if (setModuleAddedId) {
        this.addedModuleId = module.name;
      }
      this.saveAndRefreshCallback({
        module,
        shouldRefresh: true
      });
    };
    this.addModuleToLayoutSectionFromInpageSidebar = ({
      message: {
        payload,
        type,
        widgetCid
      }
    }) => {
      var _module$path;
      const store = window.hubspot.ContentEditorUI.store;
      const reduxState = hubspot.ContentEditorUI.store.getState();
      const isUngatedForMergeInDefaultValues = getIsUngatedForMergeInDefaultValues(reduxState);
      const builtInMapping = getModuleIdsByBuiltinType(reduxState);
      const addAction = {
        payload,
        type
      };
      const searchParams = new URLSearchParams(location.search);
      const insertFormGuid = searchParams.get('insertFormGuid');
      const schemaForId = getSchemaForId(reduxState, widgetCid);
      const module = createNewModuleFromSchema(schemaForId, {
        setMergeInDefaultFieldValuesToFalse: isUngatedForMergeInDefaultValues
      });
      module.layout_section_id = payload.layoutSectionId;
      module.id = module.name;
      module.path = (_module$path = module.path) !== null && _module$path !== void 0 ? _module$path : schemaForId.path;
      if (insertFormGuid && schemaForId && isFormModule(schemaForId, builtInMapping)) {
        const formField = schemaForId.fields.find(field => field.name === 'form');
        module.body = Object.assign({}, module.body || {}, {
          form: Object.assign({}, (formField === null || formField === void 0 ? void 0 : formField.default) || {}, {
            form_id: insertFormGuid
          })
        });
        store.dispatch(clearQueryParam('insertFormGuid'));
      }
      const schema = getSchemaForId(reduxState, widgetCid);

      // The data flow here is a little convoluted. Here's what happens:
      //
      // 1. We dispatch addModuleToLayoutSection from CEUI.
      // 2. That action does a ModuleRender API call to get the HTML for the new module.
      // 3. Once retrieved, we dispatch the original add action (addAction here).
      // 4. Then we postMessage to IPEUI, and add the html to the UPDATED_MODULE_LOOKUP_MAP
      //    in renderLayoutSectionsFromTrees.
      // 5. renderLayoutSectionsFromTrees  looks at UPDATED_MODULE_LOOKUP_MAP for any element
      //    matching the new module ID, and uses it when  rendering the tree to DOM.
      //
      // fin.

      // If a module's schema has CSS or JS, make sure we refresh the IPEUI
      // iframe so that those are loaded properly in the <head> and elsewhere.
      if (moduleHasAnyJs(schema)) {
        addAction.payload.newModuleSchemaJson = createNewModuleSchemaJson(schema, module);
        if (schema && !schema.global) {
          hubspot.ContentEditorUI.store.dispatch(updateSelectedItem(module.name));
        }
        hubspot.ContentEditorUI.store.dispatch(addAction);
        this.saveAndRefreshCallback({
          module,
          shouldRefresh: true
        });
      } else {
        hubspot.ContentEditorUI.store.dispatch(addModuleToLayoutSection({
          addAction,
          module,
          schema
        })).then(resp => {
          this.bus.postMessage({
            action: 'htmlReceivedForModule',
            moduleId: module.id,
            moduleHtml: resp
          });
        }).catch(error => {
          showErrorWithHTML({
            action: 'addModuleFailed'
          });
          throw error;
        });
      }
    };
    this.addModuleToFlexColumnFromInpageSidebar = message => {
      this.addModuleToFlexColumn(message.message);
    };
    this.updateLayoutSectionToAddToFromInpage = message => {
      var _message$isEmptyLayou;
      const store = window.hubspot.ContentEditorUI.store;
      const dispatch = store.dispatch;
      dispatch(updateLayoutSectionToAddTo({
        rowId: message.payload.rowId,
        targetBeforeOrAfter: message.targetBeforeOrAfter,
        isEmptyLayoutSection: (_message$isEmptyLayou = message.isEmptyLayoutSection) !== null && _message$isEmptyLayou !== void 0 ? _message$isEmptyLayou : false,
        layoutSectionId: message.payload.layoutSectionId
      }));
    };
    this.addBuildingBlockToGridFromInpageSidebar = ({
      message
    }) => {
      const store = window.hubspot.ContentEditorUI.store;
      const dispatch = store.dispatch;
      const {
        type,
        payload
      } = message;
      if (!ALLOWED_BUILDING_BLOCK_ACTION_TYPES.has(type)) {
        console.error(`Ignoring disallowed building block action type from inpage sidebar: ${type}`);
        return;
      }
      dispatch({
        type,
        payload
      });
    };
    this.addCustomSectionFromInpageSidebar = ({
      message
    }) => {
      const store = window.hubspot.ContentEditorUI.store;
      const dispatch = store.dispatch;
      this.updateLayoutSectionToAddToFromInpage(message);
      dispatch(addCustomLayoutSection(message.sectionTree));
    };
    this.addCustomLayoutFromInpageSidebar = ({
      message
    }) => {
      const store = window.hubspot.ContentEditorUI.store;
      const dispatch = store.dispatch;
      const sections = generateLayoutDataForAllDefaultColumns();
      const sectionToAdd = sections[message.customLayoutKey];
      this.updateLayoutSectionToAddToFromInpage(message);
      dispatch(addCustomLayoutSection(sectionToAdd));
    };
    this.addEmbedFromInpageSidebar = ({
      message
    }) => {
      const store = window.hubspot.ContentEditorUI.store;
      const dispatch = store.dispatch;
      this.updateLayoutSectionToAddToFromInpage(message);
      const treeFragment = createTreeFragmentForEmbed(message.embedId);
      dispatch(addEmbedToEmbedInfo(message.embedId, message.name || ''));
      dispatch(addCustomLayoutSection(treeFragment));
    };
    this.moduleSelectCallback = (containerId, widgetCid) => {
      this.addModuleToFlexColumn({
        containerId,
        // let the reducer figure out the module order
        order: undefined,
        widgetCid,
        setModuleAddedId: true
      });
    };
    this.highlightAddedWidgetAfterDomIsReady = () => {
      if (this.addedModuleId) {
        // Scroll to newly-added widget once the dom is ready
        hubspot.ContentEditorUI.store.dispatch(updateScrollToModuleId(this.addedModuleId));
        this.addedModuleId = null;
      }
    };
    this.renderModuleSelector = opts => {
      const {
        containerId
      } = opts;
      const closeModal = () => ReactDOM.unmountComponentAtNode(document.getElementById('contentModuleSelector'));
      closeModal();
      ReactDOM.render( /*#__PURE__*/_jsx(ThemeProvider, {
        children: /*#__PURE__*/_jsx(Provider, {
          store: hubspot.ContentEditorUI.store,
          children: /*#__PURE__*/_jsx(ModuleSelectorModal, {
            containerId: containerId,
            moduleSelectCallback: this.moduleSelectCallback,
            closeModalCallback: closeModal
          })
        })
      }), document.getElementById('contentModuleSelector'));
    };
    this.bus = bus;
    this.saveActions = saveActions;
    this.bus.on(`post:${inpageActionTypes.ADD_MODULE_TO_FLEX_COLUMN_FROM_INPAGE_SIDEBAR}`, this.addModuleToFlexColumnFromInpageSidebar);
    this.bus.on(`post:${inpageActionTypes.ADD_MODULE_TO_LAYOUT_SECTION_FROM_INPAGE_SIDEBAR}`, this.addModuleToLayoutSectionFromInpageSidebar);
    this.bus.on(`post:${inpageActionTypes.ADD_BUILDING_BLOCK_TO_GRID_DND_AREA_FROM_INPAGE_SIDEBAR}`, this.addBuildingBlockToGridFromInpageSidebar);
    this.bus.on(`post:${inpageActionTypes.ADD_CUSTOM_SECTION_TO_DND_AREA_FROM_INPAGE_SIDEBAR}`, this.addCustomSectionFromInpageSidebar);
    this.bus.on(`post:${inpageActionTypes.ADD_CUSTOM_LAYOUT_TO_DND_AREA_FROM_INPAGE_SIDEBAR}`, this.addCustomLayoutFromInpageSidebar);
    this.bus.on(`post:${inpageActionTypes.ADD_EMBED_TO_DND_AREA_FROM_INPAGE_SIDEBAR}`, this.addEmbedFromInpageSidebar);
    this.bus.on('openModuleSelector', this.renderModuleSelector);
    this.bus.on('post:domready', this.highlightAddedWidgetAfterDomIsReady);
  }

  // App must refresh to get new widget markup if a module is added to a flex column
}